/**
 * The office's subscription (C27; D-261, D-030; engineer decision D-267): the Settings card "الاشتراك" (Super
 * Manager only, as the routes), the dialog with the old gate modal's look (`SubscriptionModal`), redemption of a
 * prepaid card confirmed with the Super Manager's own PIN, and the gate shown instead of the portal while the
 * office is closed / blocked (the payment page of ADR-01 §2.4). Every figure is the server's; strings only here.
 */
import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SettingsCardFrame, SubscriptionCardBody, SubscriptionModal, formatWithCommas, type SubscriptionHistoryRow, type SubscriptionView } from "@faylasuf/ui";
import type { SubscriptionHistory, SubscriptionStatus } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { keys } from "../../app/query.js";

const money = (amount: string | undefined | null, currency: string | null | undefined) => (amount == null ? null : `${formatWithCommas(amount.replace(/\.00$/, ""))} ${currency ?? ""}`.trim());

export interface SubscriptionDialog {
  readonly open: () => void;
  readonly status: SubscriptionStatus | undefined;
  readonly node: ReactNode;
}

/** The dialog and its data; `gate` = closed / blocked (cannot be closed, logout instead). */
export function useSubscriptionDialog(opts: { gate?: boolean } = {}): SubscriptionDialog {
  const { t, source, locale } = useApp();
  const { session, isSuperManager, refresh, sessionLost, logout } = useSession();
  const qc = useQueryClient();
  const pin = usePinGate();
  const [isOpen, setOpen] = useState(false);
  const visible = isOpen || opts.gate === true;
  const q = useQuery({ queryKey: keys.subscription, queryFn: () => source.subscription(), enabled: isSuperManager });
  const h = useQuery({ queryKey: [...keys.subscription, "history"], queryFn: () => source.subscriptionHistory(), enabled: isSuperManager && visible });
  if ((q.error && isSessionLost(q.error)) || (h.error && isSessionLost(h.error))) sessionLost();

  const tz = session.office.timeZone;
  const fmtAt = (iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso)).replace(",", "");
  const s = q.data;
  const notice = session.licence ?? null;
  const lineLabel = (item: string, quantity: number) =>
    item === "base" ? t("subscription.line.base") : item === "users" ? t("subscription.line.users", { n: quantity }) : item === "branches" ? t("subscription.line.branches", { n: quantity }) : t(`subscription.line.${item.replace("addon:", "addon_")}`);

  const view: SubscriptionView | null = s
    ? s.licensed
      ? {
          officeName: session.office.name,
          state: s.state!,
          coverEndsOn: s.coverEndsOn ?? null,
          daysLeft: s.daysLeft ?? null,
          lapseDay: s.lapseDay ?? null,
          nextState: s.next ?? null,
          unlimited: s.unlimited === true,
          priceListName: s.priceList?.name ?? null,
          balance: s.priceList ? money(s.balance, s.currency) : null,
          period: s.currentPeriod ? { from: s.currentPeriod.startsOn, to: s.currentPeriod.endsOn, users: s.currentPeriod.users, branches: s.currentPeriod.branches } : null,
          upcomingPeriods: s.upcomingPeriods?.length ?? 0,
          dailyLines: (s.dailyDue?.lines ?? []).filter((l) => l.amount !== "0.00").map((l) => ({ label: lineLabel(l.item, l.quantity), amount: money(l.amount, s.currency)! })),
          dailyTotal: s.dailyDue ? money(s.dailyDue.total, s.currency) : null,
          usage: { users: s.usage?.users ?? 0, branches: s.usage?.branches ?? 1 },
          lockedMinutes: s.redeemLockedUntil ? Math.max(1, Math.ceil((new Date(s.redeemLockedUntil).getTime() - Date.now()) / 60_000)) : null,
        }
      : { officeName: session.office.name, state: "active", coverEndsOn: null, daysLeft: null, lapseDay: null, nextState: null, unlimited: true, priceListName: null, balance: null, period: null, upcomingPeriods: 0, dailyLines: [], dailyTotal: null, usage: { users: 0, branches: 1 }, lockedMinutes: null }
    : !isSuperManager && notice
      ? { officeName: session.office.name, state: notice.state, coverEndsOn: notice.coverEndsOn, daysLeft: notice.daysLeft, lapseDay: notice.lapseDay, nextState: null, unlimited: notice.coverEndsOn === null && notice.state === "active", priceListName: null, balance: null, period: null, upcomingPeriods: 0, dailyLines: [], dailyTotal: null, usage: { users: 0, branches: 1 }, lockedMinutes: null }
      : null;

  const history: SubscriptionHistoryRow[] = historyRows(h.data, t, fmtAt, s?.currency ?? null);

  const onRedeem = async (code: string): Promise<string | null> => {
    try {
      const r = await pin.withPin((proof) => source.redeemCard(code, proof), { message: t("subscription.pin_message") });
      if (r === undefined) return "";
      await qc.invalidateQueries({ queryKey: keys.subscription });
      await refresh();
      void qc.invalidateQueries();
      return null;
    } catch (e) {
      if (isSessionLost(e)) {
        sessionLost();
        return "";
      }
      await qc.invalidateQueries({ queryKey: keys.subscription });
      return errorMessage(t, e);
    }
  };

  void locale;
  const node = visible ? (
    <>
      <SubscriptionModal
        view={view}
        loading={isSuperManager && q.isLoading}
        canRedeem={isSuperManager && s?.licensed === true}
        onRedeem={onRedeem}
        history={history}
        isGateLock={opts.gate === true}
        onClose={() => setOpen(false)}
        onLogout={() => void logout()}
      />
      {pin.dialog}
    </>
  ) : null;
  return { open: () => setOpen(true), status: s, node };
}

function historyRows(h: SubscriptionHistory | undefined, t: (k: string, p?: Record<string, string | number>) => string, fmtAt: (iso: string) => string, currency: string | null): SubscriptionHistoryRow[] {
  if (!h) return [];
  const out: SubscriptionHistoryRow[] = [];
  for (const e of h.events) {
    const d = e.detail as Record<string, unknown>;
    const at = fmtAt(e.at);
    switch (e.kind) {
      case "redeemed":
        out.push(d["kind"] === "balance" ? { at, label: t("subscription.ev.redeemed_balance"), amount: `+${money(String(d["amount"]), String(d["currency"] ?? currency ?? ""))}`, tone: "plus" } : { at, label: t("subscription.ev.redeemed_period", { from: String(d["startsOn"]), to: String(d["endsOn"]) }), tone: "plus" });
        break;
      case "daily_charge":
        out.push({ at, label: t("subscription.ev.daily_charge", { date: String(d["date"]) }), amount: `−${money(String(d["amount"]), String(d["currency"] ?? currency ?? ""))}`, tone: "minus" });
        break;
      case "day_unpaid":
        out.push({ at, label: t("subscription.ev.day_unpaid", { date: String(d["date"]) }), tone: "warn" });
        break;
      case "state_changed":
        out.push({ at, label: t("subscription.ev.state_changed", { state: t(`subscription.state.${String(d["to"])}`) }), tone: d["to"] === "active" ? "neutral" : "warn" });
        break;
      case "warning":
        out.push({ at, label: t("subscription.ev.warning", { n: Number(d["daysLeft"] ?? 0) }), tone: "warn" });
        break;
      case "redeem_failed":
      case "redeem_locked":
        out.push({ at, label: t(`subscription.ev.${e.kind}`), tone: "warn" });
        break;
      default:
        out.push({ at, label: t(`subscription.ev.${e.kind}`), tone: "neutral" });
    }
  }
  return out;
}

/** Settings → النظام → "الاشتراك" (Super Manager only). */
export function SubscriptionContainer({ customIcon }: { customIcon?: string }) {
  const { t } = useApp();
  const dlg = useSubscriptionDialog();
  const s = dlg.status;
  const summary = !s
    ? t("subscription.loading")
    : !s.licensed || s.unlimited
      ? t("subscription.unlimited")
      : s.state === "active"
        ? t("subscription.cover_until", { date: s.coverEndsOn ?? "—", n: s.daysLeft ?? 0 })
        : t(`subscription.state_text.${s.state!}`, { day: s.lapseDay ?? 0 });
  return (
    <SettingsCardFrame cardKey="subscription" {...(customIcon ? { customIcon } : {})}>
      <SubscriptionCardBody state={s ? (s.licensed ? s.state! : "active") : null} summary={summary} onOpen={dlg.open} />
      {dlg.node}
    </SettingsCardFrame>
  );
}
